import { useState } from 'react';
import { RwGlyph, RwStatusPill } from '@components/atoms';
import { RwLoading } from '@components/RwLoading';
import { RwEmpty } from '@components/RwEmpty';
import { t } from '@services/i18n';
import { useApiQuery } from '@hooks/useApiQuery';
import { apiPost } from '@services/api-client';
import { redemptionStateMap } from '@shared/data/fixtures';
import type { Me, CatalogItem, Redemption } from '@shared/types';
import toast from 'react-hot-toast';
import type { MemberScreenProps } from './types';

interface StoreResponse {
  me: Me;
  catalog: CatalogItem[];
  redemptions: Redemption[];
}

// ── Catalogue ───────────────────────────────────────────────────────────────
export function UserStore(_props: MemberScreenProps) {
  const { data, refetch, isLoading } = useApiQuery<StoreResponse>(['rw-store'], 'store', {
    fallback: { me: undefined as unknown as Me, catalog: [], redemptions: [] },
  });
  const me = data?.me;
  const points = me?.points ?? 0;
  const catalog = data?.catalog ?? [];
  const redemptions = data?.redemptions ?? [];

  const [redeeming, setRedeeming] = useState<string | null>(null);
  const redeem = async (item: CatalogItem): Promise<void> => {
    setRedeeming(item.id);
    try {
      await apiPost('redeem', { itemId: item.id });
      toast.success(t('COM_REWARDIFY_USTORE_REDEEMED', 'Redeemed — see your activity for the reservation'));
      void refetch();
    } catch {
      toast.error(t('COM_REWARDIFY_USTORE_REDEEM_FAILED', 'Could not redeem this reward'));
    } finally {
      setRedeeming(null);
    }
  };

  if (isLoading) return <RwLoading variant="cards" />;

  return (
    <div className="rw-page rw-user">
      <header className="rw-page-h">
        <div>
          <div className="rw-eyebrow">{t('COM_REWARDIFY_USTORE_EYEBROW', 'Redemption')}</div>
          <h1 className="rw-h1">{t('COM_REWARDIFY_USTORE_TITLE', 'Catalogue')}</h1>
          <p className="rw-lede">{t('COM_REWARDIFY_USTORE_LEDE', 'Spend Community Points on perks and rewards. If fulfilment ever fails, your points are returned automatically.')}</p>
        </div>
        <div className="rw-balance">
          <div>
            <div className="rw-balance-label">{t('COM_REWARDIFY_USTORE_SPENDABLE_POINTS', 'Spendable points')}</div>
            <div className="rw-balance-amount">{points.toLocaleString()}</div>
          </div>
        </div>
      </header>

      {catalog.filter((s) => s.release !== 'Later').length === 0 ? (
        <RwEmpty
          title={t('COM_REWARDIFY_USTORE_EMPTY_TITLE', 'No rewards available yet')}
          hint={t('COM_REWARDIFY_USTORE_EMPTY_HINT', 'Rewards will appear here once the catalogue is published.')}
        />
      ) : (
      <div className="rw-store-grid">
        {catalog.filter((s) => s.release !== 'Later').map((s) => {
          const affordable = points >= s.price;
          const pct = s.price > 0 ? Math.min(100, Math.round((points / s.price) * 100)) : 100;
          return (
            <div key={s.id} className={`rw-store-card ${!affordable ? 'rw-locked' : ''}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div className="rw-store-icon"><RwGlyph kind={s.icon} size={24} /></div>
                <span className="rw-store-kind">{s.kind}</span>
              </div>
              <h4 className="rw-store-name">{s.name}</h4>
              <p className="rw-store-desc">{s.desc}</p>
              <div className="rw-store-progress">
                <div className="rw-store-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                  <div className={`rw-store-bar-fill ${affordable ? 'is-ready' : ''}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="rw-store-bar-meta">
                  <span className="rw-mono">
                    {points.toLocaleString()} / {s.price.toLocaleString()} {t('COM_REWARDIFY_USTORE_CP', 'CP')}
                  </span>
                  <span className={affordable ? 'rw-store-ready' : ''}>
                    {affordable ? t('COM_REWARDIFY_USTORE_READY', 'Ready to redeem') : `${pct}%`}
                  </span>
                </div>
              </div>
              <div className="rw-store-footer">
                <div>
                  <div className="rw-store-price-l">{t('COM_REWARDIFY_USTORE_PRICE', 'Price')}</div>
                  <div className="rw-store-price">{s.price} <span>{t('COM_REWARDIFY_USTORE_CP', 'CP')}</span></div>
                </div>
                <button
                  type="button"
                  className={`rw-btn ${affordable ? 'rw-btn-primary' : ''}`}
                  disabled={!affordable || redeeming === s.id}
                  onClick={() => redeem(s)}
                >
                  {affordable
                    ? t('COM_REWARDIFY_USTORE_REDEEM', 'Redeem')
                    : t('COM_REWARDIFY_USTORE_NEED_MORE', 'Need %s more', (s.price - points).toLocaleString())}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      )}

      <h3 className="rw-section-t">{t('COM_REWARDIFY_USTORE_HISTORY_TITLE', 'My redemption history')}</h3>
      {redemptions.length === 0 ? (
        <RwEmpty
          title={t('COM_REWARDIFY_USTORE_HISTORY_EMPTY_TITLE', 'No redemptions yet')}
          hint={t('COM_REWARDIFY_USTORE_HISTORY_EMPTY_HINT', 'Rewards you redeem will appear here.')}
        />
      ) : (
      <section className="rw-card" style={{ padding: 0 }}>
        <table className="rw-table">
          <thead>
            <tr>
              <th>{t('COM_REWARDIFY_USTORE_COL_ITEM', 'Item')}</th>
              <th style={{ textAlign: 'right' }}>{t('COM_REWARDIFY_USTORE_COL_SPENT', 'Spent')}</th>
              <th>{t('COM_REWARDIFY_USTORE_COL_STATUS', 'Status')}</th>
              <th style={{ textAlign: 'right' }}>{t('COM_REWARDIFY_USTORE_COL_WHEN', 'When')}</th>
            </tr>
          </thead>
          <tbody>
            {redemptions.map((r) => {
              const stt = redemptionStateMap[r.state];
              return (
                <tr key={r.id}>
                  <td style={{ fontSize: 13 }}>
                    {r.item}
                    <div className="rw-fg2 rw-mono" style={{ fontSize: 10.5 }}>{r.code ? t('COM_REWARDIFY_USTORE_CODE', 'code %s', r.code) : r.note}</div>
                  </td>
                  <td className="rw-mono" style={{ textAlign: 'right' }}>{r.state === 'released' ? t('COM_REWARDIFY_USTORE_RETURNED', '0 (returned)') : `−${r.price}`} {t('COM_REWARDIFY_USTORE_CP', 'CP')}</td>
                  <td>{stt && <RwStatusPill label={stt.label} tone={stt.tone} dot sm />}</td>
                  <td className="rw-fg2 rw-mono" style={{ textAlign: 'right', fontSize: 11 }}>{r.at}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      )}
    </div>
  );
}
